import { useState, useRef, useEffect } from 'react';
import { PlayIcon, PauseIcon, SpeakerWaveIcon, SpeakerXMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

/**
 * AudioPlayer - Audio playback component for TTS responses
 *
 * Plays audio from blob URL (MP3 from ElevenLabs)
 * Supports play/pause, mute/unmute controls
 */
const AudioPlayer = ({ audioUrl, autoPlay = true }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);

  // Auto-play when audio URL is set
  useEffect(() => {
    if (audioUrl && audioRef.current) {
      setIsLoading(true);
      setError(null);
      setIsPlaying(false);

      // Stop and reset any currently playing audio first
      const audio = audioRef.current;
      if (!audio.paused) {
        audio.pause();
      }
      audio.currentTime = 0;

      // Load new audio
      audio.load();

      // Auto-play will be handled by onCanPlay event, not here
      if (!autoPlay) {
        setIsLoading(false);
      }
    }

    // Cleanup: pause audio when component unmounts or URL changes
    return () => {
      if (audioRef.current && !audioRef.current.paused) {
        audioRef.current.pause();
      }
    };
  }, [audioUrl]);

  // Play audio
  const playAudio = async () => {
    if (!audioRef.current) return;

    try {
      setIsLoading(true);
      await audioRef.current.play();
      setIsPlaying(true);
      setIsLoading(false);
      console.log('🔊 Audio playback started');
    } catch (err) {
      console.error('Audio playback error:', err);
      setError('Failed to play audio');
      setIsLoading(false);
    }
  };

  // Pause audio
  const pauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      console.log('⏸️ Audio playback paused');
    }
  };

  // Toggle mute
  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !audioRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  // Handle audio events
  const handleEnded = () => {
    setIsPlaying(false);
    console.log('✅ Audio playback completed');
  };

  const handleCanPlay = () => {
    setIsLoading(false);
    // Auto-play if enabled
    if (autoPlay) {
      playAudio();
    }
  };

  const handleError = (e) => {
    console.error('Audio error:', e);
    setError('Failed to load audio');
    setIsLoading(false);
    setIsPlaying(false);
  };

  if (!audioUrl) {
    return null;
  }

  return (
    <div className="flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-lg">
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        src={audioUrl}
        onEnded={handleEnded}
        onCanPlay={handleCanPlay}
        onError={handleError}
        className="hidden"
      />

      {/* Play/Pause Button */}
      <button
        onClick={isPlaying ? pauseAudio : playAudio}
        disabled={isLoading || error}
        className={`
          p-2 rounded-full transition-colors
          ${isLoading || error
            ? 'bg-gray-300 cursor-not-allowed'
            : 'bg-blue-500 hover:bg-blue-600 text-white'
          }
        `}
        title={isPlaying ? 'Pause' : 'Play'}
      >
        {isLoading ? (
          <ArrowPathIcon className="w-5 h-5 animate-spin" />
        ) : isPlaying ? (
          <PauseIcon className="w-5 h-5" />
        ) : (
          <PlayIcon className="w-5 h-5" />
        )}
      </button>

      {/* Mute/Unmute Button */}
      <button
        onClick={toggleMute}
        disabled={isLoading || error}
        className="p-2 rounded-full bg-gray-300 hover:bg-gray-400 transition-colors"
        title={isMuted ? 'Unmute' : 'Mute'}
      >
        {isMuted ? (
          <SpeakerXMarkIcon className="w-5 h-5 text-gray-700" />
        ) : (
          <SpeakerWaveIcon className="w-5 h-5 text-gray-700" />
        )}
      </button>

      {/* Status Text */}
      <div className="flex-1 text-sm text-gray-700">
        {isLoading && <span className="italic">Loading audio...</span>}
        {error && <span className="text-red-500">{error}</span>}
        {!isLoading && !error && isPlaying && <span>Playing...</span>}
        {!isLoading && !error && !isPlaying && <span>Audio ready</span>}
      </div>
    </div>
  );
};

export default AudioPlayer;
